import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import AiAgentIcon from '../assets/images/module/general/AiAgentIcon.svg';

// The AI chat on phones/tablets: a full-screen sheet instead of the desktop's draggable,
// resizable Rnd window (dragging and corner-resizing don't work with a finger, and an
// 880px window doesn't fit a phone). While collapsed only a floating button stays on screen.
//
// The chat itself (ChatAppModal) stays mounted the whole time and is only hidden, same as
// the desktop window's `visibility: hidden` — closing the sheet must not drop the
// conversation, the typed draft or a running request.
//
// Children are a render function getting the sheet's content size: ChatApp sizes its
// message list and input area off explicit pixel width/height props, not CSS.

// Height of what's actually visible. On iOS/Android the on-screen keyboard shrinks the
// visual viewport but not the layout viewport (100vh / innerHeight), so sizing off
// innerHeight would leave the input field behind the keyboard.
const readViewport = () => {
  const vv = window.visualViewport;
  return {
    height: vv ? vv.height : window.innerHeight,
    offsetTop: vv ? vv.offsetTop : 0,
  };
};

const MobileAiChatPanel = ({ expanded, showLauncher, onOpen, children }) => {
  const [viewport, setViewport] = useState(readViewport);
  const [contentSize, setContentSize] = useState({ width: 0, height: 0 });
  const contentRef = useRef(null);

  useEffect(() => {
    const update = () => setViewport(readViewport());
    const vv = window.visualViewport;
    window.addEventListener('resize', update);
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    return () => {
      window.removeEventListener('resize', update);
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
    };
  }, []);

  useLayoutEffect(() => {
    const node = contentRef.current;
    if (!node) return undefined;
    const measure = () => setContentSize({ width: node.clientWidth, height: node.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // The page behind the sheet must not scroll along with the chat (iOS otherwise scrolls the
  // body once the message list hits its end). The bottom nav (.app-sidebar, z-index 999999)
  // and Intercom's launcher would otherwise sit on top of the sheet's input row; the class
  // hides the nav (styles.scss) the same way `hide-appbar` does for the app's own modals.
  useEffect(() => {
    if (!expanded) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('ai-chat-mobile-open');
    window.Intercom?.('update', { hide_default_launcher: true });
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove('ai-chat-mobile-open');
      window.Intercom?.('update', { hide_default_launcher: false });
    };
  }, [expanded]);

  return (
    <>
      <div
        className={`ai-chat-mobile-panel ${expanded ? 'expanded' : ''}`}
        style={{ top: viewport.offsetTop, height: viewport.height }}
        aria-hidden={!expanded}
      >
        <div ref={contentRef} className="ai-chat-mobile-panel-content">
          {contentSize.height > 0 && children(contentSize)}
        </div>
      </div>
      {!expanded && showLauncher &&
      <button type="button" className="ai-chat-mobile-fab" onClick={onOpen} aria-label="hellohousing KI öffnen">
        <img src={AiAgentIcon} alt="" />
        <span>KI</span>
      </button>}
    </>
  );
};

export default MobileAiChatPanel;
